/**
 * Personalized Weather API for v2.1 with activity modes and user preferences
 */

const API_BASE_URL = 'https://develop.layers.today/.netlify/functions';

/**
 * Get personalized weather recommendations using v2.1 API
 * @param {Object} params - Request parameters
 * @param {number} params.latitude - Latitude coordinate
 * @param {number} params.longitude - Longitude coordinate
 * @param {number} params.forecastHours - Hours ahead for forecast (0 = current)
 * @param {string} params.activityMode - Activity mode: walk, run, cycle, travel
 * @param {Object} params.userPreferences - User preferences from context
 * @param {Date} params.selectedDate - Selected date for weather (optional)
 * @returns {Promise<Object>} Weather recommendation response
 */
export const getPersonalizedWeatherRecommendations = async ({
  latitude,
  longitude,
  forecastHours = 0,
  activityMode = 'walk',
  userPreferences = {},
  selectedDate = null
}) => {
  try {
    // Calculate forecast hours if selectedDate is provided
    let adjustedForecastHours = forecastHours;
    if (selectedDate && selectedDate instanceof Date) {
      const now = new Date();
      const diffTime = selectedDate.getTime() - now.getTime();
      const diffHours = Math.round(diffTime / (1000 * 60 * 60));
      
      // Clamp to reasonable forecast range (-24 to 168 hours)
      adjustedForecastHours = Math.max(-24, Math.min(168, diffHours));
    }

    // Build query parameters
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      forecastHours: adjustedForecastHours.toString(),
      mode: activityMode
    });

    // Add user preferences if available
    if (userPreferences.layer_shift !== undefined) {
      params.append('layer_shift', userPreferences.layer_shift.toString());
    }
    if (userPreferences.vote_shift !== undefined) {
      params.append('vote_shift', userPreferences.vote_shift.toString());
    }
    if (activityMode === 'run' || activityMode === 'cycle') {
      params.append('intensity', '2'); // Default intensity for active modes
    }

    console.log('🌤️ Racing weather APIs for instant recommendations...');

    // Create abort controllers for racing APIs
    const primaryController = new AbortController();
    const fallbackController = new AbortController();

    // Primary API call (Personalized v2.1)
    const primaryApiCall = fetch(
      `${API_BASE_URL}/getWeatherRecommendationsStandalone?${params}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: primaryController.signal
      }
    ).then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        
        // Add metadata about the request
        data.requestMetadata = {
          activityMode,
          forecastHours: adjustedForecastHours,
          selectedDate: selectedDate?.toISOString(),
          userPreferences: {
            layer_shift: userPreferences.layer_shift || 0,
            vote_shift: userPreferences.vote_shift || 0
          },
          apiVersion: '2.1-personalized',
          source: 'primary'
        };
        
        return { source: 'primary', data };
      }
      throw new Error(`Primary weather API failed: ${response.status}`);
    });

    // Fallback API call (Basic Open-Meteo) - starts after 3 seconds for weather
    const fallbackApiCall = new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          console.log('🌤️ Starting fallback weather API after 3s delay...');
          
          // Import the basic weather logic
          const { getLayerRecommendations } = await import('./layerRecommendationsApi');
          const result = await getLayerRecommendations({ 
            latitude, 
            longitude, 
            forecastHours: adjustedForecastHours 
          });
          
          // Format to match primary API structure
          const fallbackData = {
            weather: {
              temperature: result.temperature,
              conditions: result.conditions
            },
            recommendation: result.recommendation,
            requestMetadata: {
              activityMode,
              forecastHours: adjustedForecastHours,
              selectedDate: selectedDate?.toISOString(),
              userPreferences: {
                layer_shift: userPreferences.layer_shift || 0,
                vote_shift: userPreferences.vote_shift || 0
              },
              apiVersion: 'basic-fallback',
              source: 'fallback'
            }
          };
          
          resolve({ source: 'fallback', data: fallbackData });
        } catch (error) {
          reject(error);
        }
      }, 3000); // 3 second delay for weather API
    });

    try {
      // Race both APIs - first one to succeed wins
      console.log('🌤️ Racing primary personalized and fallback basic APIs...');
      const result = await Promise.race([
        primaryApiCall.catch(err => {
          console.warn('🌤️ Primary weather API failed:', err.message);
          throw err;
        }),
        fallbackApiCall.catch(err => {
          console.warn('🌤️ Fallback weather API failed:', err.message);
          throw err;
        })
      ]);
      
      // Cancel the other request
      if (result.source === 'primary') {
        console.log('🌤️ Primary weather API won the race!');
        fallbackController.abort();
      } else {
        console.log('🌤️ Fallback weather API won the race!');
        primaryController.abort();
      }
      
      console.log('🌤️ Weather data received from', result.source);
      return result.data;
      
    } catch (error) {
      // Both APIs failed - clean up and throw
      console.warn('🌤️ Both weather APIs failed:', error);
      primaryController.abort();
      fallbackController.abort();
      throw error;
    }

  } catch (error) {
    console.error('Weather API racing failed:', error);
    throw error;
  }
};

/**
 * Submit user feedback for layer recommendations
 * @param {Object} feedbackData - Feedback data
 * @param {string} feedbackData.vote - User vote: 'too_cold', 'just_right', 'too_warm'
 * @param {Object} feedbackData.coordinates - Location coordinates
 * @param {Object} feedbackData.weather - Weather data from API response
 * @param {Object} feedbackData.recommendation - Recommendation data
 * @param {string} feedbackData.activityMode - Activity mode
 * @param {number} feedbackData.forecastHours - Forecast hours
 * @returns {Promise<Object>} Submission response
 */
export const submitLayerFeedback = async (feedbackData) => {
  try {
    // Map emoji votes to numeric values for API
    const voteMapping = {
      'too_cold': -1,
      'just_right': 0,
      'too_warm': 1
    };

    const payload = {
      session_id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      activity_mode: feedbackData.activityMode,
      temperature: feedbackData.weather?.temperature?.apparent,
      felt_temp: feedbackData.weather?.temperature?.actual,
      wind_speed: feedbackData.weather?.conditions?.windSpeed || 0,
      humidity: feedbackData.weather?.conditions?.relativeHumidity || 50,
      precipitation: feedbackData.weather?.conditions?.precipitation || 0,
      delta_layer: voteMapping[feedbackData.vote] || 0,
      recommended_layers: feedbackData.recommendation?.layers,
      algorithm_version: 'v2.1-personalized',
      user_agent: navigator.userAgent,
      coordinates: feedbackData.coordinates
    };

    const response = await fetch(`${API_BASE_URL}/submitVote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Feedback submission failed: ${response.status} ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Feedback submission error:', error);
    throw error;
  }
};